import {
  useCurrentTimePosition,
  getTodayWeekday,
} from '../hooks/use-current-time-position.ts';
import type { Weekday } from '@/features/plants/schemas/schedule-plant.schema.ts';
import {
  SCHEDULE_HOURS_IN_DAY,
  SCHEDULE_MINUTES_IN_HOUR,
} from '@/features/plants/components/plant-schedule/schedule/constants.ts';

function formatMinuteOfDay(minuteOfDay: number): string {
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

interface ScheduleTimeCursorProps {
  gridRef: React.RefObject<HTMLDivElement | null>;
  dayRefs: React.RefObject<{ [key in Weekday]?: HTMLDivElement | null }>;
  scheduleWidth: number;
  dayColumnWidthPx: number;
}

export function ScheduleTimeCursor({
  gridRef,
  dayRefs,
  scheduleWidth,
  dayColumnWidthPx,
}: ScheduleTimeCursorProps) {
  const minuteOfDay = useCurrentTimePosition();
  const todayWeekday = getTodayWeekday();

  /* eslint-disable react-hooks/refs -- Ref reads during render are intentional: cursor
     must recalculate position each time minuteOfDay changes. */
  const gridEl = gridRef.current;
  const dayEl = dayRefs.current?.[todayWeekday];

  if (!gridEl || !dayEl) return null;

  const gridRect = gridEl.getBoundingClientRect();
  const dayRect = dayEl.getBoundingClientRect();

  const rowTop = dayRect.top - gridRect.top;
  const rowHeight = dayRect.height;
  /* eslint-enable react-hooks/refs */

  const totalMinutes = SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR;
  const leftPx = (minuteOfDay / totalMinutes) * scheduleWidth;

  const formattedTime = formatMinuteOfDay(minuteOfDay);

  return (
    <div
      className="pointer-events-none absolute z-10"
      style={{
        top: rowTop,
        left: dayColumnWidthPx + leftPx - 1,
        width: 2,
        height: rowHeight,
      }}
    >
      {/* Always-visible time badge anchored to the top of the cursor line */}
      <div className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap">
        <div className="flex items-center gap-1 rounded border bg-white px-1.5 py-0.5 shadow-sm">
          <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
          <span className="text-xs font-semibold text-slate-700 tabular-nums">
            {formattedTime}
          </span>
        </div>
      </div>

      <div className="h-full w-full rounded-full bg-red-500 opacity-80" />
    </div>
  );
}
